import type { Color, ToastButton } from "@ionic/core";
import type { UseIonToastResult } from "@ionic/react";
import { Cubit } from "blac";
import translate from "src/lib/translate";
import type { TranslationKey } from "src/types/translationKey";
import type { ReactElement } from "react";
import { createRoot } from "react-dom/client";

type ToastState = {
  linked: boolean;
};

export default class ToastBloc extends Cubit<ToastState> {
  ionicToast?: UseIonToastResult;

  constructor() {
    super({
      linked: false
    });
  }

  linkToast = (ionicToast: UseIonToastResult | null) => {
    if (this.ionicToast !== undefined || ionicToast === null) {
      return;
    }

    this.ionicToast = ionicToast;
    this.emit({ ...this.state, linked: true });
  };

  private renderIconInToast = (
    shadowRoot: ShadowRoot,
    icon: ReactElement,
    isTransparentTheme: boolean
  ) => {
    const container = shadowRoot.querySelector(".toast-container");
    if (!container) {
      return;
    }

    const iconContainer = document.createElement("div");
    if (isTransparentTheme) {
      iconContainer.style.display = "flex";
      iconContainer.style.alignItems = "center";
    }
    container.insertBefore(iconContainer, container.firstChild);
    const root = createRoot(iconContainer);
    root.render(icon);
  };

  private applyToastStyles = (
    options: {
      cssClass?: string;
      buttons?: (ToastButton | string)[];
      icon?: ReactElement;
    },
    isTransparentTheme: boolean,
    icon?: ReactElement
  ) => {
    const toastSelector = options.cssClass
      ? `ion-toast.${options.cssClass.split(" ")[0]}`
      : "ion-toast.ion-color-success";
    const toastEl =
      document.querySelector(toastSelector) ??
      document.querySelector("ion-toast");

    if (!toastEl?.shadowRoot) {
      return;
    }

    const hasButtons = options.buttons && options.buttons.length > 0;
    if (hasButtons || icon) {
      const toastContent = toastEl.shadowRoot.querySelector(
        ".toast-content"
      ) as HTMLElement;

      toastContent.style.flex = "unset";
      if (isTransparentTheme) {
        toastContent.style.padding = "0";
      }
    }

    if (icon) {
      this.renderIconInToast(toastEl.shadowRoot, icon, isTransparentTheme);
    }
  };

  show = (
    message: TranslationKey,
    options: {
      severity?: Color;
      duration?: number;
      translationVariables?: Record<string, unknown>;
      buttons?: (ToastButton | string)[];
      onDidDismiss?: () => void;
      icon?: ReactElement;
      cssClass?: string;
    } = {}
  ) => {
    if (!this.ionicToast) {
      return;
    }
    const { severity, duration = 7000, icon } = options;

    const isTransparentTheme =
      options.cssClass?.includes("toast-transparent") ?? false;

    const isMobile = window.innerWidth < 768;

    let position: "top" | "bottom";
    if (isTransparentTheme) {
      position = "top";
    } else if (isMobile) {
      position = "bottom";
    } else {
      position = "top";
    }

    const msg = translate(message, options.translationVariables);

    const [present] = this.ionicToast;

    let cssClasses = options.cssClass ?? "";
    if (options.buttons && options.buttons.length > 0) {
      cssClasses += " toast-with-buttons";
    }
    if (icon) {
      cssClasses += " toast-with-icon";
    }

    void present({
      message: msg,
      duration,
      color: severity,
      position,
      buttons: options.buttons,
      positionAnchor: isTransparentTheme ? "top" : "bottom",
      onDidDismiss: options.onDidDismiss,
      keyboardClose: true,
      swipeGesture: "vertical",
      cssClass: cssClasses.trim()
    }).then(() => {
      requestAnimationFrame(() => {
        this.applyToastStyles(options, isTransparentTheme, icon);
      });
    });
  };

  error = (
    message: TranslationKey,
    translationVariables?: Record<string, unknown>
  ) => {
    this.show(message, { severity: "danger", translationVariables });
  };

  success = (message: TranslationKey, icon?: ReactElement) => {
    this.show(message, { severity: "success", icon });
  };

  dismiss = () => {
    if (!this.ionicToast) {
      return;
    }
    const [, dismiss] = this.ionicToast;
    void dismiss();
  };
}
